import { Gym } from 'fitbeat.models';
import React from 'react';
import { MemberTableHeader } from '../members/components/memberTableHeader';
import { TeamsDropdown } from './components/teamsDropdown';

export const buildLeaderboardInstanceColumnsFromAccessor = (
  accessor: string,
  currentGym: Gym,
) => {
  switch (accessor) {
    case 'rank':
      return {
        Header: () => <MemberTableHeader header='Position' />,
        accessor,
        used: true,
        headerClassName: 'container-overflow-visible',
        sortable: true,
      };
    case 'leagueName':
      return {
        Header: () => <MemberTableHeader header='League Name' />,
        accessor,
        used: true,
        headerClassName: 'container-overflow-visible',
        sortable: true,
      };
    case 'rankWithinLeague':
      return {
        Header: () => <MemberTableHeader header='League Rank' />,
        accessor,
        used: true,
        headerClassName: 'container-overflow-visible',
        sortable: true,
      };
    case 'name':
      return {
        Header: () => <MemberTableHeader header='Name' />,
        accessor,
        used: true,
        headerClassName: 'container-overflow-visible',
        sortable: true,
      };
    case 'lastname':
      return {
        Header: () => <MemberTableHeader header='Last Name' />,
        accessor,
        used: true,
        headerClassName: 'container-overflow-visible',
        sortable: true,
      };
    case 'email':
      return {
        Header: () => <MemberTableHeader header='Email' />,
        accessor,
        used: true,
        headerClassName: 'container-overflow-visible',
        sortable: true,
      };
    case 'value':
      return {
        Header: () => <MemberTableHeader header='Score' />,
        accessor,
        used: true,
        headerClassName: 'container-overflow-visible',
        sortable: true,
        Cell: ({ original }: any) => Math.round(original.value * 100) / 100,
      };
    case 'teamName':
      return {
        Header: () => <MemberTableHeader header='Team' />,
        accessor,
        used: true,
        headerClassName: 'container-overflow-visible',
        sortable: true,
        Cell: ({ original }: any) => {
          const defaultTeamName = original.currentTeam || '';
          return (
            <TeamsDropdown
              activeTeamId={defaultTeamName}
              uid={original.userProfileId}
            />
          );
        },
      };
    case 'memberDetails':
      return {
        Header: () => <MemberTableHeader header='Details' />,
        accessor,
        used: true,
        headerClassName: 'container-overflow-visible',
        sortable: false,
        filterAll: false,
        filterable: false,
        Cell: ({ original }: any) => {
          if (!original.userProfileId) {
            return null;
          }
          const openUserDetails = () => {
            window.open(`/#/app/memberDetails/${original.userProfileId}`, '_blank'); return false;
          };
          return (
            <button
              onClick={openUserDetails}
              className='jr-btn-default-outlined'
              role='rowgroup'
            >
              Member
            </button>
          );
        },
      };
    case 'totalCompleted':
      return statsColumn(accessor, 'Workouts Done');
    case 'totalDurationInMinutes':
      return statsColumn(accessor, 'Workout Minutes');
    case 'stepsTaken':
      return statsColumn(accessor, 'Steps Taken');
    case 'xpSumPoints-all':
      return statsColumn(accessor, 'Total XP');
    case 'xpSumPoints-category-exercise':
      return statsColumn(accessor, 'Exercise XP');
    case 'xpSumPoints-category-nutrition':
      return statsColumn(accessor, 'Nutrition XP');
    case 'xpSumPoints-category-social':
      return statsColumn(accessor, 'Social XP');
    case 'xpSumPoints-category-education':
      return statsColumn(accessor, 'Education XP');
    case 'xpSumPoints-category-achievement':
      return statsColumn(accessor, 'Achievements XP');
    default:
      return {};
  }
};

const statsColumn = (accessor: string, header: string) => ({
  Header: () => <MemberTableHeader header={header} />,
  accessor,
  used: true,
  headerClassName: 'container-overflow-visible',
  sortable: true,
  Cell: ({ original }: any) => {
    const value = original?.[accessor];
    if (value === undefined || value === null) {
      return '-';
    }
    return Math.round(value);
  },
});

export const leaderboardInstanceAccessors = [
  {
    accessor: 'memberDetails',
    used: true,
    sortable: true,
    width: 110,
  },
  {
    accessor: 'rank',
    used: true,
    sortable: true,
    width: 80,
  },
  {
    accessor: 'leagueName',
    used: true,
    sortable: true,
    width: 140,
  },
  {
    accessor: 'rankWithinLeague',
    used: true,
    sortable: true,
    width: 100,
  },
  {
    accessor: 'value',
    used: true,
    sortable: true,
    width: 100,
  },
  {
    accessor: 'name',
    used: true,
    sortable: true,
    width: 150,
  },
  {
    accessor: 'lastname',
    used: true,
    sortable: true,
    width: 150,
  },
  {
    accessor: 'email',
    used: true,
    sortable: true,
    width: 220,
  },
  {
    accessor: 'xpSumPoints-all',
    used: true,
    sortable: true,
    width: 130,
  },
  {
    accessor: 'xpSumPoints-category-exercise',
    used: true,
    sortable: true,
    width: 170,
  },
  {
    accessor: 'xpSumPoints-category-nutrition',
    used: true,
    sortable: true,
    width: 170,
  },
  {
    accessor: 'xpSumPoints-category-social',
    used: true,
    sortable: true,
    width: 170,
  },
  {
    accessor: 'xpSumPoints-category-education',
    used: true,
    sortable: true,
    width: 170,
  },
  {
    accessor: 'xpSumPoints-category-achievement',
    used: true,
    sortable: true,
    width: 180,
  },
  {
    accessor: 'stepsTaken',
    used: true,
    sortable: true,
    width: 150,
  },
  {
    accessor: 'totalCompleted',
    used: true,
    sortable: true,
    width: 180,
  },
  {
    accessor: 'totalDurationInMinutes',
    used: true,
    sortable: true,
    width: 180,
  },
  {
    accessor: 'teamName',
    used: true,
    sortable: true,
    width: 150,
  }];

const alwaysVisibleInstanceAccessorIds = new Set(['memberDetails', 'rank', 'value', 'name', 'teamName']);

const hasValueForAccessor = (row: Record<string, unknown>, accessor: string) => {
  const value = row[accessor];
  return value !== undefined && value !== null && value !== '';
};

/** Omit optional columns when no row has values for them (no dummy metric/league columns). */
export function getLeaderboardInstanceAccessorsForRankings(
  rankings: ReadonlyArray<Record<string, unknown>> | undefined | null,
) {
  return leaderboardInstanceAccessors.filter((col) =>
    alwaysVisibleInstanceAccessorIds.has(col.accessor)
    || !!rankings?.some((row) => hasValueForAccessor(row, col.accessor)));
}
